import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import pharmacyService from '../../../services/pharmacyService';
import Icon from '../../common/Icons';
import { formatDate, formatNumber } from '../ui';
import { stockStatusLabel } from '../pharmacyCopy';
import { suggestOrderQty } from './SmartOrderComposer';
import LotSelectField from './LotSelectField';
import { lotExpiryTag, lotExpiryTone } from './lotExpiryUi';
import { searchMedications } from './medicationSearch';
import { HighlightedText } from './smartPickerControls';

function stockPillClass(status) {
  if (status === 'OUT_OF_STOCK') return 'cp-status-pill--danger';
  if (status === 'LOW_STOCK') return 'cp-status-pill--warn';
  return 'cp-status-pill--ok';
}

function lineKey(line, idx) {
  if (line.source_lot_id) return `l-${line.source_lot_id}`;
  return line.medication_id ? `m-${line.medication_id}-${idx}` : `i-${idx}`;
}

function sortLotsFefo(lots) {
  return [...lots].sort((a, b) => {
    const aExp = a.expiry_date ? new Date(a.expiry_date).getTime() : Number.MAX_SAFE_INTEGER;
    const bExp = b.expiry_date ? new Date(b.expiry_date).getTime() : Number.MAX_SAFE_INTEGER;
    if (aExp !== bExp) return aExp - bExp;
    return Number(a.id) - Number(b.id);
  });
}

function activeLots(lots) {
  return sortLotsFefo(
    (lots || []).filter((lot) => Number(lot.quantity) > 0)
  );
}

/** Quantité proposée : besoin site dest, plafonnée par le lot choisi. */
export function suggestDistQty(destStock, lotQty) {
  const available = Number(lotQty || 0);
  if (available <= 0) return 1;
  const qty = destStock ? suggestOrderQty(destStock) : Math.min(10, available);
  return Math.max(1, Math.min(qty, available));
}

function buildLineFromLot(med, lot, destStock) {
  const maxQty = Number(lot.quantity || 0);
  return {
    medication_id: String(med.id),
    source_lot_id: String(lot.id),
    lot_number: lot.lot_number || '',
    expiry_date: lot.expiry_date ? String(lot.expiry_date).slice(0, 10) : '',
    quantity: String(suggestDistQty(destStock, maxQty))
  };
}

/**
 * Formulaire de création de distribution (centrale → site) avec choix du lot.
 */
export default function SmartDistributionComposer({
  medications = [],
  destSites = [],
  centralSiteId,
  value,
  onChange,
  copy,
  linesOnly = false,
  lockDestination = false
}) {
  const [centralStockByMed, setCentralStockByMed] = useState({});
  const [destStockByMed, setDestStockByMed] = useState({});
  const [centralLotsByMed, setCentralLotsByMed] = useState({});
  const centralLotsRef = useRef(centralLotsByMed);
  centralLotsRef.current = centralLotsByMed;
  const [stockLoading, setStockLoading] = useState(false);
  const [destStockLoading, setDestStockLoading] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLotRows, setPickerLotRows] = useState([]);
  const [pickerLotsLoading, setPickerLotsLoading] = useState(false);
  const pickerRef = useRef(null);

  const selectedLotIds = useMemo(
    () => new Set(value.items.map((l) => String(l.source_lot_id)).filter(Boolean)),
    [value.items]
  );

  const medById = useMemo(() => {
    const map = {};
    medications.forEach((m) => {
      map[String(m.id)] = m;
    });
    return map;
  }, [medications]);

  const fetchLotsForMed = useCallback(
    async (medId) => {
      const key = String(medId);
      if (centralLotsRef.current[key]) return centralLotsRef.current[key];
      if (!centralSiteId) return [];
      const res = await pharmacyService.getMedicationLots(medId, {
        site_id: centralSiteId,
        stock_location: 'MAIN'
      });
      const lots = activeLots(res.success ? res.data || [] : []);
      setCentralLotsByMed((prev) => ({ ...prev, [key]: lots }));
      return lots;
    },
    [centralSiteId]
  );

  useEffect(() => {
    if (!centralSiteId) {
      setCentralStockByMed({});
      setCentralLotsByMed({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setStockLoading(true);
      try {
        const res = await pharmacyService.getSiteStock(centralSiteId);
        if (cancelled) return;
        const map = {};
        (res.success ? res.data || [] : []).forEach((row) => {
          map[String(row.medication_id)] = row;
        });
        setCentralStockByMed(map);
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [centralSiteId]);

  useEffect(() => {
    if (!value.dest_site_id) {
      setDestStockByMed({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setDestStockLoading(true);
      try {
        const res = await pharmacyService.getSiteStock(value.dest_site_id);
        if (cancelled) return;
        const map = {};
        (res.success ? res.data || [] : []).forEach((row) => {
          map[String(row.medication_id)] = row;
        });
        setDestStockByMed(map);
      } finally {
        if (!cancelled) setDestStockLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value.dest_site_id]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!pickerRef.current?.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const patch = useCallback(
    (key, next) => onChange({ ...value, [key]: next }),
    [onChange, value]
  );

  const patchItems = useCallback(
    (items) => onChange({ ...value, items }),
    [onChange, value]
  );

  const pickerMedResults = useMemo(() => {
    const withStock = medications.filter((m) => {
      const central = centralStockByMed[String(m.id)];
      return central && Number(central.on_hand || 0) > 0;
    });
    return searchMedications(withStock, pickerQuery, { limit: 8 });
  }, [medications, pickerQuery, centralStockByMed]);

  useEffect(() => {
    if (!pickerOpen || !centralSiteId) {
      setPickerLotRows([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setPickerLotsLoading(true);
      try {
        const rows = [];
        for (const med of pickerMedResults) {
          const lots = await fetchLotsForMed(med.id);
          if (cancelled) return;
          for (const lot of lots) {
            if (!selectedLotIds.has(String(lot.id))) {
              rows.push({ med, lot });
            }
          }
        }
        if (!cancelled) setPickerLotRows(rows.slice(0, 24));
      } finally {
        if (!cancelled) setPickerLotsLoading(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [pickerOpen, pickerMedResults, centralSiteId, selectedLotIds, fetchLotsForMed]);

  useEffect(() => {
    value.items.forEach((line) => {
      if (line.medication_id) fetchLotsForMed(line.medication_id);
    });
  }, [value.items, fetchLotsForMed]);

  const addLotLine = (med, lot) => {
    if (selectedLotIds.has(String(lot.id))) return;
    const dest = destStockByMed[String(med.id)];
    const line = buildLineFromLot(med, lot, dest);
    const kept = value.items.filter((l) => l.source_lot_id);
    patchItems([line, ...kept]);
    setPickerQuery('');
    setPickerOpen(false);
  };

  const updateLine = (idx, patchLine) => {
    patchItems(value.items.map((line, i) => (i === idx ? { ...line, ...patchLine } : line)));
  };

  const onLotChange = (idx, lotId) => {
    const line = value.items[idx];
    const lots = centralLotsByMed[String(line.medication_id)] || [];
    const lot = lots.find((l) => String(l.id) === String(lotId));
    if (!lot || selectedLotIds.has(String(lot.id)) && String(line.source_lot_id) !== String(lotId)) {
      return;
    }
    const maxQty = Number(lot.quantity || 0);
    let qty = parseInt(line.quantity, 10) || 1;
    if (qty > maxQty) qty = maxQty;
    updateLine(idx, {
      source_lot_id: String(lot.id),
      lot_number: lot.lot_number || '',
      expiry_date: lot.expiry_date ? String(lot.expiry_date).slice(0, 10) : '',
      quantity: String(Math.max(1, qty))
    });
  };

  const removeLine = (idx) => {
    const next = value.items.filter((_, i) => i !== idx);
    patchItems(next.length ? next : []);
  };

  const destNeedsSuggestions = useMemo(() => {
    if (!value.dest_site_id) return [];
    return Object.values(destStockByMed)
      .filter((s) => s.stock_status === 'LOW_STOCK' || s.stock_status === 'OUT_OF_STOCK')
      .filter((s) => {
        const central = centralStockByMed[String(s.medication_id)];
        return central && Number(central.on_hand || 0) > 0;
      })
      .sort((a, b) => Number(a.on_hand) - Number(b.on_hand));
  }, [value.dest_site_id, destStockByMed, centralStockByMed]);

  const fillFromDestNeeds = async () => {
    if (!destNeedsSuggestions.length) return;
    const toAdd = [];
    const usedLotIds = new Set(selectedLotIds);
    for (const s of destNeedsSuggestions.slice(0, 40)) {
      const med = medById[String(s.medication_id)] || {
        id: s.medication_id,
        name: s.medication_name,
        unit_name: s.unit_name
      };
      const lots = await fetchLotsForMed(med.id);
      const lot = lots.find((l) => !usedLotIds.has(String(l.id)));
      if (!lot) continue;
      usedLotIds.add(String(lot.id));
      toAdd.push(buildLineFromLot(med, lot, s));
    }
    if (!toAdd.length) return;
    const kept = value.items.filter((l) => l.source_lot_id);
    patchItems([...toAdd, ...kept]);
  };

  const validCount = value.items.filter(
    (l) => l.medication_id && l.source_lot_id && l.quantity
  ).length;

  const assistLoading = stockLoading || (value.dest_site_id && destStockLoading);
  const assistHint = !centralSiteId
    ? copy.SMART_NO_CENTRAL
    : !value.dest_site_id
      ? copy.SMART_SELECT_DEST
      : assistLoading
        ? copy.SMART_STOCK_LOADING
        : copy.SMART_DEST_HINT.replace('{n}', String(destNeedsSuggestions.length));

  return (
    <div className="cp-smart-order">
      {!linesOnly ? (
        <section className="cp-form-section">
          <h3 className="cp-form-section__title">{copy.SECTION_HEADER}</h3>
          <div className="cp-form-grid">
            <Form.Group className="cp-form-field">
              <Form.Label>{copy.FIELD_DEST}</Form.Label>
              {lockDestination ? (
                <Form.Control
                  plaintext
                  readOnly
                  className="form-control-plaintext"
                  value={destSites.find((s) => String(s.id) === String(value.dest_site_id))?.name || '—'}
                />
              ) : (
                <Form.Select
                  value={value.dest_site_id}
                  onChange={(e) => patch('dest_site_id', e.target.value)}
                >
                  <option value="">— Choisir —</option>
                  {destSites.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}
                    </option>
                  ))}
                </Form.Select>
              )}
              <Form.Text>{copy.FIELD_DEST_HINT}</Form.Text>
            </Form.Group>
            <Form.Group className="cp-form-field cp-form-field--full">
              <Form.Label>{copy.FIELD_NOTES}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={value.notes}
                onChange={(e) => patch('notes', e.target.value)}
                placeholder="Référence interne, remarques…"
                readOnly={lockDestination && linesOnly}
              />
            </Form.Group>
          </div>
        </section>
      ) : null}

      <section className="cp-form-section cp-smart-order__lines">
        <div className="cp-form-section__head">
          <h3 className="cp-form-section__title mb-0">{copy.SECTION_LINES}</h3>
          <span className="cp-form-section__meta">
            {copy.LINES_COUNT.replace('{n}', String(validCount))}
          </span>
        </div>

        <div className="cp-smart-order__assist">
          <div className="cp-smart-order__assist-text">
            <Icon name="bell" size={15} aria-hidden />
            <span>{assistHint}</span>
          </div>
          <button
            type="button"
            className="cp-btn cp-btn--ghost cp-btn--sm"
            disabled={!value.dest_site_id || !destNeedsSuggestions.length}
            onClick={fillFromDestNeeds}
          >
            <Icon name="plus" size={14} aria-hidden />
            {copy.SMART_FILL_NEEDS}
            {destNeedsSuggestions.length ? ` (${destNeedsSuggestions.length})` : ''}
          </button>
        </div>

        <div className="cp-smart-picker" ref={pickerRef}>
          <label className="cp-form-field mb-0 w-100">
            <span className="form-label">{copy.SMART_ADD_LOT_LABEL}</span>
            <div className="cp-smart-picker__control">
              <Icon name="search" size={16} className="cp-smart-picker__icon" aria-hidden />
              <input
                type="search"
                className="cp-smart-picker__input"
                placeholder={copy.SMART_ADD_PLACEHOLDER}
                value={pickerQuery}
                onChange={(e) => {
                  setPickerQuery(e.target.value);
                  setPickerOpen(true);
                }}
                onFocus={() => setPickerOpen(true)}
                autoComplete="off"
                disabled={!centralSiteId}
              />
            </div>
          </label>
          {pickerOpen ? (
            <ul className="cp-smart-picker__list" role="listbox">
              {pickerLotsLoading ? (
                <li className="cp-smart-picker__empty">{copy.SMART_LOTS_LOADING}</li>
              ) : pickerLotRows.length === 0 ? (
                <li className="cp-smart-picker__empty">{copy.SMART_NO_MATCH}</li>
              ) : (
                pickerLotRows.map(({ med, lot }) => {
                  const dest = destStockByMed[String(med.id)];
                  return (
                    <li key={lot.id}>
                      <button
                        type="button"
                        className="cp-smart-picker__option"
                        onClick={() => addLotLine(med, lot)}
                      >
                        <span className="cp-smart-picker__option-main">
                          <strong>
                            <HighlightedText text={med.name} query={pickerQuery} />
                          </strong>
                          <span className="cp-smart-picker__option-meta">
                            {copy.LOT_PICKER_META.replace('{lot}', lot.lot_number || '—')
                              .replace('{qty}', formatNumber(lot.quantity))
                              .replace('{exp}', lot.expiry_date ? formatDate(lot.expiry_date) : '—')}
                          </span>
                        </span>
                        <span className="cp-smart-picker__option-end">
                          {lot.expiry_date ? (
                            <span className={`cp-expiry-chip ${lotExpiryTone(lot.expiry_date)}`}>
                              <span className="cp-expiry-chip__date">{formatDate(lot.expiry_date)}</span>
                              <span className="cp-expiry-chip__tag">{lotExpiryTag(lot.expiry_date)}</span>
                            </span>
                          ) : null}
                          {dest ? (
                            <span className={`cp-status-pill ${stockPillClass(dest.stock_status)}`}>
                              {stockStatusLabel(dest.stock_status)}
                            </span>
                          ) : (
                            <span className="cp-smart-picker__add">{copy.SMART_ADD_CTA}</span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          ) : null}
        </div>

        {value.items.filter((l) => l.medication_id).length === 0 ? (
          <div className="cp-smart-order__empty">
            <Icon name="arrowRight" size={22} aria-hidden />
            <p className="mb-0">{copy.SMART_EMPTY_LINES}</p>
          </div>
        ) : (
          <div className="cp-table-wrap cp-smart-lines-table-wrap">
            <table className="cp-table cp-smart-lines-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>{copy.COL_MED}</th>
                  <th>{copy.COL_LOT_SELECT}</th>
                  <th>{copy.COL_EXPIRY}</th>
                  <th className="text-end">{copy.COL_LOT_AVAILABLE}</th>
                  <th className="text-end" style={{ minWidth: 110 }}>
                    {copy.SMART_QTY}
                  </th>
                  <th className="text-end" style={{ width: 52 }} />
                </tr>
              </thead>
              <tbody>
                {value.items
                  .map((line, idx) => ({ line, idx }))
                  .filter(({ line }) => line.medication_id)
                  .map(({ line, idx }, displayIdx, rows) => {
                    const med = medById[String(line.medication_id)];
                    const lots = centralLotsByMed[String(line.medication_id)] || [];
                    const selectedLot = lots.find(
                      (l) => String(l.id) === String(line.source_lot_id)
                    );
                    const name =
                      med?.name ||
                      centralStockByMed[String(line.medication_id)]?.medication_name ||
                      `Médicament #${line.medication_id}`;
                    const rowNum = rows.length - displayIdx;
                    const lotOnHand = Number(selectedLot?.quantity || 0);
                    const qty = parseInt(line.quantity, 10) || 0;
                    const overLot = selectedLot && qty > lotOnHand;

                    return (
                      <tr
                        key={lineKey(line, idx)}
                        className={overLot ? 'cp-smart-lines-table__row--warn' : ''}
                      >
                        <td className="cp-num text-muted">{rowNum}</td>
                        <td>
                          <strong className="cp-smart-lines-table__name">{name}</strong>
                          <div className="cp-smart-lines-table__meta text-muted small">
                            {med?.unit_name || '—'}
                          </div>
                          {overLot ? (
                            <div className="cp-smart-lines-table__warn small text-danger">
                              {copy.OVER_LOT.replace('{n}', formatNumber(lotOnHand))}
                            </div>
                          ) : null}
                          {!line.source_lot_id ? (
                            <div className="cp-smart-lines-table__warn small text-danger">
                              {copy.LOT_REQUIRED}
                            </div>
                          ) : null}
                        </td>
                        <td className="cp-smart-lines-table__lot-cell">
                          <LotSelectField
                            lots={lots}
                            value={line.source_lot_id || ''}
                            onChange={(lotId) => onLotChange(idx, lotId)}
                            disabledLotIds={selectedLotIds}
                            placeholder={copy.LOT_SELECT_PLACEHOLDER}
                            copy={copy}
                            ariaLabel={`${copy.COL_LOT_SELECT} ${name}`}
                          />
                        </td>
                        <td>
                          {line.expiry_date || selectedLot?.expiry_date ? (
                            <span
                              className={`cp-expiry-chip ${lotExpiryTone(line.expiry_date || selectedLot?.expiry_date)}`}
                            >
                              <span className="cp-expiry-chip__date">
                                {formatDate(line.expiry_date || selectedLot?.expiry_date)}
                              </span>
                              <span className="cp-expiry-chip__tag">
                                {lotExpiryTag(line.expiry_date || selectedLot?.expiry_date)}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-end cp-num">
                          {selectedLot ? formatNumber(selectedLot.quantity) : '—'}
                        </td>
                        <td className="text-end">
                          <Form.Control
                            type="number"
                            min={1}
                            max={lotOnHand > 0 ? lotOnHand : undefined}
                            size="sm"
                            className="cp-smart-lines-table__qty text-end"
                            value={line.quantity}
                            onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                            aria-label={`${copy.SMART_QTY} ${name}`}
                            disabled={!line.source_lot_id}
                          />
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="cp-btn cp-btn--danger cp-btn--sm"
                            onClick={() => removeLine(idx)}
                            aria-label={copy.SMART_REMOVE}
                          >
                            <Icon name="trash" size={13} aria-hidden />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
